import React, { useState, useEffect } from 'react';
import type { View } from '../../types';
import { SearchIcon, FilterIcon, PlusIcon, MoreIcon, ContentIcon, CloseIcon, ChevronLeftIcon } from '../icons';
import { useFolders, useCreateFolder, useUpdateFolder, useDeleteFolder } from '../../lib/api/hooks/folder';
import { useGalleryFolder, useDeleteGallery } from '../../lib/api/hooks/gallery';
import { showToast } from '../../lib/toast';

interface PhotoGalleryProps {
  setView: (view: View) => void;
  initialFolderId?: string;
}

const Modal: React.FC<{ isOpen: boolean; onClose: () => void; children: React.ReactNode }> = ({ isOpen, onClose, children }) => {
    if (!isOpen) return null;
    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center" onClick={onClose} role="dialog" aria-modal="true">
            <div className="bg-white rounded-xl shadow-lg w-full max-w-md m-4" onClick={(e) => e.stopPropagation()}>
                {children}
            </div>
        </div>
    );
};

const ImageViewerModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  images: string[];
  currentIndex: number;
  onNext: () => void;
  onPrev: () => void;
}> = ({ isOpen, onClose, images, currentIndex, onNext, onPrev }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center" onClick={onClose}>
      <button
        onClick={onClose}
        className="absolute top-4 right-4 text-white hover:text-gray-300 z-50"
      >
        <CloseIcon className="w-8 h-8" />
      </button>

      {images.length > 1 && currentIndex > 0 && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onPrev();
          }}
          className="absolute left-4 text-white hover:text-gray-300 z-50"
        >
          <ChevronLeftIcon className="w-12 h-12" />
        </button>
      )}

      <div className="max-w-5xl max-h-[90vh] p-4" onClick={(e) => e.stopPropagation()}>
        <img
          src={images[currentIndex]}
          alt={`Image ${currentIndex + 1}`}
          className="max-w-full max-h-[90vh] object-contain rounded-lg"
        />
        {images.length > 1 && (
          <div className="text-center text-white mt-4">
            {currentIndex + 1} / {images.length}
          </div>
        )}
      </div>

      {images.length > 1 && currentIndex < images.length - 1 && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onNext();
          }}
          className="absolute right-4 text-white hover:text-gray-300 z-50 rotate-180"
        >
          <ChevronLeftIcon className="w-12 h-12" />
        </button>
      )}
    </div>
  );
};

const CreateFolderModal: React.FC<{ onClose: () => void; onCreate: (folderName: string) => void; isCreating: boolean }> = ({ onClose, onCreate, isCreating }) => {
    const [folderName, setFolderName] = useState('');

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (folderName.trim()) {
            onCreate(folderName.trim());
        }
    };

    return (
        <div className="p-6">
            <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold text-gray-800">Folder</h2>
                <button onClick={onClose} className="text-gray-500 hover:text-gray-800" disabled={isCreating}>
                    <CloseIcon className="w-6 h-6" />
                </button>
            </div>
            <p className="text-sm text-gray-500 mb-6">Create a folder to start uploading photos</p>
            <form onSubmit={handleSubmit}>
                <label htmlFor="folder-name" className="text-sm font-medium text-gray-700">Folder Name</label>
                <input
                    id="folder-name"
                    type="text"
                    value={folderName}
                    onChange={(e) => setFolderName(e.target.value)}
                    className="w-full mt-1 px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#047857] focus:border-transparent"
                    disabled={isCreating}
                    placeholder="Enter folder name"
                />
                <button
                    type="submit"
                    className="w-full mt-6 bg-[#047857] hover:bg-[#059669] text-white font-semibold py-2.5 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={isCreating || !folderName.trim()}
                >
                    {isCreating ? 'Creating...' : 'Create'}
                </button>
            </form>
        </div>
    );
};

const EditFolderModal: React.FC<{
  onClose: () => void;
  onUpdate: (folderName: string) => void;
  isUpdating: boolean;
  initialName: string;
}> = ({ onClose, onUpdate, isUpdating, initialName }) => {
    const [folderName, setFolderName] = useState(initialName);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (folderName.trim()) {
            onUpdate(folderName.trim());
        }
    };

    return (
        <div className="p-6">
            <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold text-gray-800">Edit Folder</h2>
                <button onClick={onClose} className="text-gray-500 hover:text-gray-800" disabled={isUpdating}>
                    <CloseIcon className="w-6 h-6" />
                </button>
            </div>
            <p className="text-sm text-gray-500 mb-6">Update the folder name</p>
            <form onSubmit={handleSubmit}>
                <label htmlFor="edit-folder-name" className="text-sm font-medium text-gray-700">Folder Name</label>
                <input
                    id="edit-folder-name"
                    type="text"
                    value={folderName}
                    onChange={(e) => setFolderName(e.target.value)}
                    className="w-full mt-1 px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#047857] focus:border-transparent"
                    disabled={isUpdating}
                    placeholder="Enter folder name"
                />
                <button
                    type="submit"
                    className="w-full mt-6 bg-[#047857] hover:bg-[#059669] text-white font-semibold py-2.5 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={isUpdating || !folderName.trim()}
                >
                    {isUpdating ? 'Updating...' : 'Update'}
                </button>
            </form>
        </div>
    );
};


const PhotoGallery: React.FC<PhotoGalleryProps> = ({ setView, initialFolderId }) => {
  const [activeFolderId, setActiveFolderId] = useState<string | null>(initialFolderId || null);
  const [isModalOpen, setModalOpen] = useState(false);
  const [isEditFolderModalOpen, setEditFolderModalOpen] = useState(false);
  const [editingFolder, setEditingFolder] = useState<{ id: string; name: string } | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(null);
  const [currentGalleryPhotos, setCurrentGalleryPhotos] = useState<string[]>([]);

  // Fetch folders with pagination
  const { data: foldersData, isLoading: isFoldersLoading } = useFolders({ page: 1, size: 50 });
  const folders = foldersData?.data?.data || [];

  // Fetch folder details when a folder is selected
  const { data: folderDetailData, isLoading: isFolderDetailLoading } = useGalleryFolder(activeFolderId || '');
  const folderDetail = folderDetailData?.data;

  // Folder mutations
  const createFolderMutation = useCreateFolder();
  const updateFolderMutation = useUpdateFolder();
  const deleteFolderMutation = useDeleteFolder();

  // Gallery mutations
  const deleteGalleryMutation = useDeleteGallery();
  // const updateGalleryMutation = useUpdateGallery();

  // Update active folder when initialFolderId changes
  useEffect(() => {
    if (initialFolderId) {
      setActiveFolderId(initialFolderId);
    }
  }, [initialFolderId]);

  const handleCreateFolder = async (folderName: string) => {
    try {
      const result = await createFolderMutation.mutateAsync({ folderName });

      if (result.success && result.data) {
        showToast.success('Folder created successfully');
        setModalOpen(false);
        // Stay on the folder list view - don't navigate
      } else {
        showToast.error('Failed to create folder');
      }
    } catch (error) {
      console.error('Error creating folder:', error);
      showToast.error('Failed to create folder');
    }
  };

  const handleFolderClick = (folderId: string) => {
    setActiveFolderId(folderId);
    setView({ page: 'Photo Gallery', subPage: 'folder', id: folderId });
  };

  const handleBackToFolders = () => {
    setActiveFolderId(null);
    setView({ page: 'Photo Gallery' });
  };

  const handleDeleteGallery = async (galleryId: string) => {
    if (!window.confirm('Are you sure you want to delete this gallery?')) {
      return;
    }

    try {
      const result = await deleteGalleryMutation.mutateAsync(galleryId);
      if (result.success) {
        showToast.success('Gallery deleted successfully');
      } else {
        showToast.error('Failed to delete gallery');
      }
    } catch (error) {
      console.error('Error deleting gallery:', error);
      showToast.error('Failed to delete gallery');
    }
  };

  const handleEditGallery = (galleryId: string) => {
    setView({ page: 'Photo Gallery', subPage: 'edit', id: galleryId });
  };

  const handleEditFolder = (folderId: string, folderName: string) => {
    setEditingFolder({ id: folderId, name: folderName });
    setEditFolderModalOpen(true);
  };

  const handleUpdateFolder = async (folderName: string) => {
    if (!editingFolder) return;

    try {
      const result = await updateFolderMutation.mutateAsync({
        folderId: editingFolder.id,
        data: { folderName }
      });

      if (result.success) {
        showToast.success('Folder updated successfully');
        setEditFolderModalOpen(false);
        setEditingFolder(null);
      } else {
        showToast.error('Failed to update folder');
      }
    } catch (error) {
      console.error('Error updating folder:', error);
      showToast.error('Failed to update folder');
    }
  };

  const handleDeleteFolder = async (folderId: string) => {
    if (!window.confirm('Are you sure you want to delete this folder? This will delete all galleries inside it.')) {
      return;
    }

    try {
      const result = await deleteFolderMutation.mutateAsync(folderId);
      if (result.success) {
        showToast.success('Folder deleted successfully');
      } else {
        showToast.error('Failed to delete folder');
      }
    } catch (error) {
      console.error('Error deleting folder:', error);
      showToast.error('Failed to delete folder');
    }
  };

  const openImageViewer = (photos: string[], index: number) => {
    setCurrentGalleryPhotos(photos);
    setSelectedImageIndex(index);
  };

  const closeImageViewer = () => {
    setSelectedImageIndex(null);
    setCurrentGalleryPhotos([]);
  };

  const goToNextImage = () => {
    if (selectedImageIndex !== null && selectedImageIndex < currentGalleryPhotos.length - 1) {
      setSelectedImageIndex(selectedImageIndex + 1);
    }
  };

  const goToPrevImage = () => {
    if (selectedImageIndex !== null && selectedImageIndex > 0) {
      setSelectedImageIndex(selectedImageIndex - 1);
    }
  };

  const EmptyState: React.FC = () => (
      <div className="text-center py-20">
          <div className="inline-block bg-gray-200/20 p-4 rounded-full">
             <ContentIcon className="w-10 h-10 text-gray-400" />
          </div>
          <h3 className="mt-4 font-semibold text-gray-800 text-lg">No Folders</h3>
          <p className="mt-1 text-sm text-gray-500">Create a folder to start uploading photos</p>
          <button
            onClick={() => setModalOpen(true)}
            className="mt-6 bg-[#047857] hover:bg-[#059669] text-white font-semibold py-2.5 px-5 rounded-lg transition-colors text-sm inline-flex items-center gap-2"
          >
            <PlusIcon /> Create a Folder
        </button>
      </div>
  );

  const LoadingState: React.FC = () => (
    <div className="text-center py-20">
      <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-[#047857]"></div>
      <p className="mt-4 text-sm text-gray-500">Loading...</p>
    </div>
  );

  // Filter folders based on search term
  const filteredFolders = folders.filter((folder: any) =>
    folder.folderName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <>
      <div className="space-y-6">
        {/* Top Header - Published badge and Create Folder button */}
        {!activeFolderId && (
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 p-1   rounded-lg">
              <button className="px-4 py-2 text-sm font-semibold rounded-sm bg-wine-100 text-white">
                Published 
              </button>
            </div>
            <button
              onClick={() => setModalOpen(true)}
              className="bg-[#047857] hover:bg-[#059669] text-white font-semibold py-2.5 px-4 rounded-lg text-sm"
            >
              Create Folder
            </button>
          </div>
        )}

        {/* Breadcrumb for folder view */}
        {activeFolderId && folderDetail && (
          <div className="flex items-center gap-2 text-sm">
            <button
              onClick={handleBackToFolders}
              className="text-[#047857] hover:underline font-medium"
            >
              Photo Gallery
            </button>
            <ChevronLeftIcon className="w-4 h-4 rotate-180 text-gray-400" />
            <span className="text-gray-700 font-medium">{folderDetail.folderName}</span>
          </div>
        )}

        {/* Search and Filter row */}
        <div className="flex items-center justify-between gap-4">
          <div className="relative w-80">
            <input
              type="text"
              placeholder="Search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-0  focus:outline-none focus:border-transparent"
            />
            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
              <SearchIcon className="w-4 h-4 text-gray-400" />
            </div>
          </div>
          <div className="flex items-center gap-4">
            <button className="flex items-center gap-2 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-lg px-4 py-2 hover:bg-gray-50">
              <FilterIcon className="w-4 h-4" /> Filter
            </button>
            {activeFolderId && (
              <button
                onClick={() => setView({ page: 'Photo Gallery', subPage: 'new', id: activeFolderId })}
                className="bg-[#047857] hover:bg-[#059669] text-white font-semibold py-2.5 px-4 rounded-lg text-sm"
              >
                Upload
              </button>
            )}
          </div>
        </div>

        <div className=" p-5">
            {activeFolderId ? (
             
                isFolderDetailLoading ? (
                  <LoadingState />
                ) : folderDetail && folderDetail.titleResponseList && folderDetail.titleResponseList.length > 0 ? (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                    {folderDetail.titleResponseList.map((gallery: any, galleryIndex: number) => (
                      gallery.photoUrls.map((photoUrl: string, photoIndex: number) => {
                      
                        const allPhotos = folderDetail.titleResponseList.flatMap((g: any) => g.photoUrls);
                        const globalPhotoIndex = folderDetail.titleResponseList
                          .slice(0, galleryIndex)
                          .reduce((acc: number, g: any) => acc + g.photoUrls.length, 0) + photoIndex;

                        return (
                          <div key={`${galleryIndex}-${photoIndex}`} className="group p-5 border border-gray-200 ">
                            <div
                              className="relative cursor-pointer"
                              onClick={() => openImageViewer(allPhotos, globalPhotoIndex)}
                            >
                              <img
                                src={photoUrl}
                                alt={gallery.title}
                                className="w-full h-48 object-cover rounded-lg"
                              />
                              {gallery.isKeyHighlight && photoIndex === 0 && (
                                <span className="absolute top-2 left-2 text-xs bg-[#047857] text-white px-2 py-1 rounded-full font-medium">
                                  Key Highlight
                                </span>
                              )}
                            </div>
                            <div className="flex items-center justify-between mt-2">
                              <div className="flex-1 min-w-0">
                                <h3 className="font-semibold text-gray-800 truncate">{gallery.title}</h3>
                                <p className="text-sm text-gray-500">
                                  {new Date(gallery.dateCreated).toLocaleDateString('en-US', {
                                    month: 'numeric',
                                    day: 'numeric',
                                    year: '2-digit'
                                  })}
                                </p>
                              </div>
                              {photoIndex === 0 && (
                                <div className="relative group/menu">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                    }}
                                    className="text-gray-500 hover:text-gray-800 p-1"
                                  >
                                    <MoreIcon className="w-5 h-5" />
                                  </button>
                                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-10 hidden group-hover/menu:block">
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleEditGallery(gallery.id);
                                      }}
                                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                                    >
                                      Edit Gallery
                                    </button>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleDeleteGallery(gallery.id);
                                      }}
                                      className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                                    >
                                      Delete Gallery
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-20">
                    <ContentIcon className="w-10 h-10 text-gray-400 mx-auto" />
                    <h3 className="mt-4 font-semibold text-gray-800">No Photos Yet</h3>
                    <p className="mt-1 text-sm text-gray-500">Upload photos to create your first gallery in this folder</p>
                    <button
                      onClick={() => setView({ page: 'Photo Gallery', subPage: 'new', id: activeFolderId })}
                      className="mt-6 bg-[#047857] hover:bg-[#059669] text-white font-semibold py-2.5 px-5 rounded-lg transition-colors text-sm inline-flex items-center gap-2"
                    >
                      <PlusIcon className="w-4 h-4" /> Upload Photos
                    </button>
                  </div>
                )
            ) : (
                // Folders List View
                isFoldersLoading ? (
                  <LoadingState />
                ) : filteredFolders.length > 0 ? (
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                        {filteredFolders.map((folder: any) => {
                          // Get the first photo from first gallery as thumbnail
                          const thumbnail = folder.titleResponseList?.[0]?.photoUrls?.[0] || 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?q=80&w=600';
                          // Check if any gallery is published
                          const hasPublished = folder.titleResponseList?.some((gallery: any) => gallery.status === 'PUBLISH') || false;
                          // Get date from first gallery
                          const dateCreated = folder.titleResponseList?.[0]?.dateCreated;

                          return (
                            <div
                              key={folder.folderId}
                              className="group p-5 border border-gray-200"
                            >
                                <div
                                  className="relative cursor-pointer "
                                  onClick={() => handleFolderClick(folder.folderId)}
                                >
                                    <img
                                      src={thumbnail}
                                      alt={folder.folderName}
                                      className="w-full h-36 object-cover rounded-lg"
                                    />
                                    <div className="absolute top-2 left-2">
                                        {hasPublished && (
                                          <div className="w-2.5 h-2.5 bg-green-500 rounded-full"></div>
                                        )}
                                    </div>
                                </div>
                                <div className="flex items-center justify-between mt-2">
                                  <div className="flex-1 min-w-0 cursor-pointer" onClick={() => handleFolderClick(folder.folderId)}>
                                    <h3 className="font-semibold text-gray-800 truncate">{folder.folderName}</h3>
                                    <p className="text-sm text-gray-500">
                                      {dateCreated ? new Date(dateCreated).toLocaleDateString('en-US', {
                                        month: 'numeric',
                                        day: 'numeric',
                                        year: '2-digit'
                                      }) : ''}
                                    </p>
                                  </div>
                                  <div className="relative group/menu">
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                      }}
                                      className="text-gray-500 hover:text-gray-800 p-1"
                                    >
                                      <MoreIcon className="w-5 h-5" />
                                    </button>
                                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-10 hidden group-hover/menu:block">
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleEditFolder(folder.folderId, folder.folderName);
                                        }}
                                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                                      >
                                        Edit Folder
                                      </button>
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleDeleteFolder(folder.folderId);
                                        }}
                                        className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                                      >
                                        Delete Folder
                                      </button>
                                    </div>
                                  </div>
                                </div>
                            </div>
                          );
                        })}
                    </div>
                ) : (
                    searchTerm ? (
                      <div className="text-center py-20">
                        <p className="text-gray-500">No folders found matching "{searchTerm}"</p>
                      </div>
                    ) : (
                      <EmptyState />
                    )
                )
            )}
        </div>
      </div>
      <Modal isOpen={isModalOpen} onClose={() => !createFolderMutation.isPending && setModalOpen(false)}>
        <CreateFolderModal
          onClose={() => setModalOpen(false)}
          onCreate={handleCreateFolder}
          isCreating={createFolderMutation.isPending}
        />
      </Modal>

      <Modal isOpen={isEditFolderModalOpen} onClose={() => !updateFolderMutation.isPending && setEditFolderModalOpen(false)}>
        <EditFolderModal
          onClose={() => {
            setEditFolderModalOpen(false);
            setEditingFolder(null);
          }}
          onUpdate={handleUpdateFolder}
          isUpdating={updateFolderMutation.isPending}
          initialName={editingFolder?.name || ''}
        />
      </Modal>

      <ImageViewerModal
        isOpen={selectedImageIndex !== null}
        onClose={closeImageViewer}
        images={currentGalleryPhotos}
        currentIndex={selectedImageIndex || 0}
        onNext={goToNextImage}
        onPrev={goToPrevImage}
      />
    </>
  );
};

export default PhotoGallery;
